import { Reference, useMutation } from '@apollo/client';
import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import AddParentBlock from '@components/automations/AddParentBlock';
import styles from '@components/automations/Automation.module.css';
import AutomationConditionGroup from '@components/automations/AutomationConditionGroup';
import TertiaryButton from '@components/buttons/TertiaryButton';
import BodyCopy from '@components/typography/BodyCopy';
import Heading3 from '@components/typography/Heading3';
import {
  AutomationConditionBlockDestroyDocument,
  AutomationConditionBlockDestroyMutation,
  AutomationConditionBlockDestroyMutationVariables,
  AutomationConditionBlockKeepBranchEmum,
  AutomationConditionGroupCreateDocument,
  AutomationConditionGroupCreateMutation,
  AutomationConditionGroupCreateMutationVariables,
  AutomationConditionGroupFragmentDoc,
  AutomationConditionGroupOperationEnum,
  AutomationFragment as AutomationType
} from '@typing/Generated';

type AutomationConditionBlock = Extract<AutomationType['blocks'][number], { __typename: 'AutomationConditionBlock' }>;

type Block = NonNullable<AutomationType['blocks'][number]>;

type Props = {
  automation: AutomationType;
  automationConditionBlock: AutomationConditionBlock;
  onBlockAdded: (block: Block) => void;
};

const AutomationConditionBlockPanel = ({ automation, automationConditionBlock, onBlockAdded }: Props) => {
  const [destroyAutomationConditionBlock] = useMutation<
    AutomationConditionBlockDestroyMutation,
    AutomationConditionBlockDestroyMutationVariables
  >(AutomationConditionBlockDestroyDocument);

  const handleDestroyBlock = useCallback(() => {
    destroyAutomationConditionBlock({
      variables: { id: automationConditionBlock.id, keepBranch: AutomationConditionBlockKeepBranchEmum.KEEP_TRUE }
    });
  }, [automationConditionBlock.id, destroyAutomationConditionBlock]);

  const [createAutomationConditionGroup] = useMutation<
    AutomationConditionGroupCreateMutation,
    AutomationConditionGroupCreateMutationVariables
  >(AutomationConditionGroupCreateDocument, {
    update: (store, { data }) => {
      if (data?.automationConditionGroupCreate?.automationConditionGroup) {
        const newConditionGroup = data.automationConditionGroupCreate.automationConditionGroup;
        store.modify({
          fields: {
            automationConditionGroups(cachedTriggerRefs: readonly Reference[]) {
              const newConditionGroupRef = store.writeFragment({
                data: newConditionGroup,
                fragment: AutomationConditionGroupFragmentDoc,
                fragmentName: 'AutomationConditionGroup'
              });
              if (newConditionGroupRef) {
                return [...cachedTriggerRefs, newConditionGroupRef];
              }
              return [...cachedTriggerRefs];
            }
          },
          id: store.identify({ ...automationConditionBlock })
        });
      }
    }
  });

  const handleAddAutomationConditionGroup = useCallback(() => {
    createAutomationConditionGroup({
      variables: {
        automationConditionBlockId: automationConditionBlock.id,
        operation: AutomationConditionGroupOperationEnum.AND
      }
    });
  }, [automationConditionBlock.id, createAutomationConditionGroup]);

  return (
    <>
      <Heading3>
        <FormattedMessage id="models.automation.conditionBlock" />
      </Heading3>

      {!automation.locked && (
        <>
          <TertiaryButton icon="trash" onClick={handleDestroyBlock} textKey={'models.automation.deleteBlock'} />
          <AddParentBlock automation={automation} childBlock={automationConditionBlock} onBlockAdded={onBlockAdded} />
        </>
      )}

      <IonGrid className="ion-no-margin ion-no-padding">
        {!automation.locked && (
          <IonRow className="ion-no-margin ion-no-padding">
            <IonCol size="auto">
              <div>
                <TertiaryButton
                  data-test="add-trigger"
                  icon="add"
                  onClick={handleAddAutomationConditionGroup}
                  textKey="pages.admin.automations.addConditionGroup"
                />
              </div>
            </IonCol>
          </IonRow>
        )}

        <IonRow className={`ion-no-padding ${styles.dataRow}`}>
          <IonCol>
            <>
              {automationConditionBlock.automationConditionGroups.map((automationConditionGroup, index) => (
                <div key={automationConditionGroup.id}>
                  <AutomationConditionGroup
                    automationConditionBlock={automationConditionBlock}
                    automationConditionGroup={automationConditionGroup}
                    locked={automation.locked}
                  />
                  {index < automationConditionBlock.automationConditionGroups.length - 1 && (
                    <div className={styles.operationType}>
                      <BodyCopy>
                        <FormattedMessage id={`models.automationConditionGroup.operations.AND`} />
                      </BodyCopy>
                    </div>
                  )}
                </div>
              ))}
            </>
          </IonCol>
        </IonRow>
      </IonGrid>
    </>
  );
};

export default AutomationConditionBlockPanel;
